import React, { useCallback, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { NumericFieldSetForm } from '@traquility/ui/diary';
import type { NumericFieldSetFormData, NumericFieldSetFormLabels } from '@traquility/ui/diary';

export interface NumericFieldSetFormContainerProps {
  /** Form action URL */
  formAction: string;
  /** HTTP method */
  formMethod: 'post' | 'patch';
  /** CSRF token */
  csrfToken: string;
  /** Initial form data */
  initialData: NumericFieldSetFormData;
  /** Back URL for cancel button */
  backUrl: string;
  /** Labels for i18n */
  labels: NumericFieldSetFormLabels;
  /** Validation errors */
  errors?: Record<string, string[]>;
}

function NumericFieldSetFormContainer({
  formAction,
  formMethod,
  csrfToken,
  initialData,
  backUrl,
  labels,
  errors = {},
}: NumericFieldSetFormContainerProps) {
  // Track which label index is being edited for icon picker
  const [editingLabelIndex, setEditingLabelIndex] = useState<number | null>(null);

  // Expose the editing label index for the icon picker modal
  React.useEffect(() => {
    (window as unknown as Record<string, unknown>).__numericFieldSetFormEditingLabelIndex = editingLabelIndex;
  }, [editingLabelIndex]);

  const handleSubmit = useCallback((data: NumericFieldSetFormData) => {
    // Create a form and submit it
    const form = document.createElement('form');
    form.method = 'post';
    form.action = formAction;

    // Add CSRF token
    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'authenticity_token';
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    // Add method override for PATCH
    if (formMethod === 'patch') {
      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'patch';
      form.appendChild(methodInput);
    }

    // Add form data
    const addInput = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    addInput('diaries_numeric_field_set[name]', data.name);
    addInput('diaries_numeric_field_set[units]', data.units);
    addInput('diaries_numeric_field_set[min_value]', String(data.minValue));
    addInput('diaries_numeric_field_set[max_value]', String(data.maxValue));

    // Add labels
    data.labels.forEach((label, index) => {
      const prefix = `diaries_numeric_field_set[labels_attributes][${index}]`;
      if (label.id) {
        addInput(`${prefix}[id]`, label.id);
      }
      if (label._destroy) {
        addInput(`${prefix}[_destroy]`, '1');
      } else {
        addInput(`${prefix}[name]`, label.name);
        addInput(`${prefix}[value]`, String(label.value));
        addInput(`${prefix}[color]`, label.color);
        addInput(`${prefix}[icon]`, label.icon);
        addInput(`${prefix}[position]`, String(label.position));
      }
    });

    document.body.appendChild(form);
    form.submit();
  }, [formAction, formMethod, csrfToken]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleLabelIconClick = useCallback((labelIndex: number, _currentIcon: string) => {
    setEditingLabelIndex(labelIndex);
  }, []);

  return (
    <NumericFieldSetForm
      initialData={initialData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onLabelIconClick={handleLabelIconClick}
      errors={errors}
      labels={labels}
    />
  );
}

// Store root reference for cleanup
let formRoot: Root | null = null;

/**
 * Mount function for rendering from Rails
 */
export function mountNumericFieldSetForm(
  container: HTMLElement,
  props: NumericFieldSetFormContainerProps
): Root {
  if (formRoot) {
    formRoot.unmount();
  }

  const root = createRoot(container);
  formRoot = root;
  root.render(<NumericFieldSetFormContainer {...props} />);
  return root;
}

/**
 * Unmount function for cleanup
 */
export function unmountNumericFieldSetForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default NumericFieldSetFormContainer;
